import * as React from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { SentEmailAfterUnlockView } from "@app/components";
import {
  ConfigContext,
  NotificationContext,
  ResetPasswordContext,
} from "@app/provider";
import { useQuery, validateEmail } from "@app/utils";
import { routes } from "@app/routing";
import { useRecaptchaSubmit, RecaptchaTokens } from "@app/hooks";

const RECAPTCHA_RESET_PASSWORD_ACTION = "reset_password";

const messages = defineMessages({
  sendEmailResetLinkSent: {
    id: "sendEmailResetLinkSent",
    defaultMessage:
      "We’ve sent you another reset link. Please follow the instructions",
  },
  sendEmailResendLinkTitle: {
    id: "sendEmailResendLinkTitle",
    defaultMessage: "Reset link sent",
  },
});

const SendEmail: React.FC = () => {
  const { resetPassword, resetPasswordData, resetPasswordIsLoading } =
    React.useContext(ResetPasswordContext);
  const { addNotification, clear } = React.useContext(NotificationContext);
  const { config } = React.useContext(ConfigContext);
  const [resendIsDisabled, setResendIsDisabled] = React.useState(false);
  const [localLoading, setLocalLoading] = React.useState(false);
  const resendTimeoutRef = React.useRef<NodeJS.Timeout>(undefined);
  const query = useQuery();
  const [email, setEmail] = React.useState(query.get("email") || "");
  const navigate = useNavigate();
  const intl = useIntl();

  const {
    acquireTokens,
    handleRecaptchaError,
    promoteToV2Challenge,
    v2Challenge,
    RecaptchaV3UnavailableError,
  } = useRecaptchaSubmit({
    action: RECAPTCHA_RESET_PASSWORD_ACTION,
    v2ContainerClassName: "send-email__recaptcha-v2",
    v2TestId: "send-email-recaptcha-v2",
  });

  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const isLoading = resetPasswordIsLoading || localLoading;

  const resendResetPassword = async (tokensOverride?: RecaptchaTokens) => {
    setLocalLoading(true);
    let captchaTokens: RecaptchaTokens;
    try {
      captchaTokens = tokensOverride ?? (await acquireTokens());
    } catch (err) {
      setLocalLoading(false);
      if (err instanceof RecaptchaV3UnavailableError) {
        promoteToV2Challenge(err.reason, async (tokens) => {
          await resendResetPassword(tokens);
        });
        return;
      }
      throw err;
    }

    try {
      const resp = await resetPassword(email, captchaTokens);
      if (resp?.email) {
        addNotification({
          title: intl.formatMessage(messages.sendEmailResendLinkTitle),
          message: intl.formatMessage(messages.sendEmailResetLinkSent),
          type: "info",
        });
      }
    } catch (err) {
      const handled = handleRecaptchaError(err, async (tokens) => {
        await resendResetPassword(tokens);
      });
      if (!handled) {
        throw err;
      }
    } finally {
      setLocalLoading(false);
    }
  };

  const resendEmail = () => {
    clear();
    setResendIsDisabled(true);

    void resendResetPassword();

    resendTimeoutRef.current = setTimeout(() => {
      setResendIsDisabled(false);

      if (resendTimeoutRef.current) {
        clearTimeout(resendTimeoutRef.current);
      }
    }, config.timeouts.resendTimeout);
  };

  React.useEffect(() => {
    if (resetPasswordData?.email) {
      setEmail(resetPasswordData.email);
    } else if (
      !query.get("email") ||
      !validateEmail(query.get("email") || "", usePlusSignInEmailAddress)
    ) {
      navigate(routes.root, {
        replace: true,
      });
    }
  }, []);

  return (
    <>
      <SentEmailAfterUnlockView
        email={email}
        handleResendEmail={resendEmail}
        resendIsDisabled={resendIsDisabled}
        isLoading={isLoading}
        tryAnotherEmailLink={routes.resetPassword}
      />
      {v2Challenge}
    </>
  );
};

export default SendEmail;
